/**
 * @module public/js/bootstrap/appConfigLoader.js
 * @summary Hydrates window.APP_CONFIG from tenant-level Firestore documents.
 *
 * @description
 * Reads `/AppConfig/telephony` for the active tenant and merges the result into
 * `window.APP_CONFIG`, exposing `smsProvider` at the top level while preserving
 * any existing configuration fields. Call this early in page bootstrap so
 * runtime helpers (for example smsProvider-aware deeplinks) can react to the
 * tenant's telephony settings.
 *
 * @param {object} options - Optional configuration for the hydration step.
 * @param {string} [options.tenantId] - Tenant identifier to scope the lookup.
 * @param {{ currentUser?: { tenantId?: string } }} [options.auth] - Auth object
 * that may carry a tenantId on the current user.
 * @param {(ref: import('firebase/firestore').DocumentReference) => Promise<import('firebase/firestore').DocumentSnapshot>} [options.fetchDoc=getDoc]
 * Custom fetcher (useful for tests) that mirrors `getDoc`.
 * @returns {Promise<Record<string, unknown>>} The merged APP_CONFIG snapshot.
 */
import { getDoc } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { docWithTenant } from '../utils/firestoreHelpers.js';

export async function hydrateAppConfig({
  tenantId,
  auth,
  fetchDoc = getDoc,
} = {}) {
  const existing =
    typeof window !== 'undefined' && typeof window.APP_CONFIG === 'object'
      ? window.APP_CONFIG
      : {};

  const resolvedTenantId =
    tenantId || auth?.currentUser?.tenantId || existing.tenantId;
  if (!resolvedTenantId) {
    window.APP_CONFIG = {
      ...existing,
      smsProvider: existing.smsProvider || 'default',
      __hydrated: true,
      __hydratedAt: Date.now(),
    };
    return window.APP_CONFIG;
  }

  let telephonySnap = null;
  let telephonyFetchFailed = false;
  try {
    telephonySnap = await fetchDoc(
      docWithTenant(resolvedTenantId, 'AppConfig/telephony')
    );
  } catch (err) {
    telephonyFetchFailed = true;
    console.warn('[appConfigLoader] failed to load /AppConfig/telephony', err);
  }

  const telephonyData = telephonySnap?.data?.() || {};

  const smsProvider = telephonyData.smsProvider
    ? String(telephonyData.smsProvider).toLowerCase()
    : existing.smsProvider || 'default';

  const callProvider = telephonyData.callProvider
    ? String(telephonyData.callProvider).toLowerCase()
    : existing.callProvider || 'twilio';

  const zoomVoiceEnabled = Boolean(
    telephonyData.zoomVoiceEnabled ?? existing.zoomVoiceEnabled ?? false
  );

  window.APP_CONFIG = {
    ...existing,
    telephony: {
      ...(existing.telephony || {}),
      ...telephonyData,
    },
    smsProvider,
    callProvider,
    zoomVoiceEnabled,
    tenantId: resolvedTenantId,
    __hydrated: true,
    __hydratedAt: Date.now(),
    __hydratedError: telephonyFetchFailed,
    __hydratedErrorReason: telephonyFetchFailed
      ? 'telephony_fetch_failed'
      : null,
  };

  return window.APP_CONFIG;
}
